import { useEffect, useMemo, useState } from 'react'
import { ChevronDown, ChevronRight, History, Link2, Plus, Trash2, Wand2 } from 'lucide-react'
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  IconButton,
  Input,
  Modal,
  Select,
  Textarea,
  useToast,
} from '@/components/ui'
import {
  useAddContractItem,
  useContract,
  useContractItemHistory,
  useContractItemPriceHistory,
  useContractUserGroups,
  useAddItemAsset,
  useAddItemUser,
  useDeleteContractItem,
  useInitItemsFromTariff,
  useProducts,
  useRemoveItemAsset,
  useRemoveItemUser,
  useContractSetupFees,
  useUpdateContractItem,
  useUpdateItemUserType,
  useContracts,
  useContractItemLinks,
  useCreateContractItemLink,
  useDeleteContractItemLink,
} from '../api/contractsApi.ts'
import type { ContractDetail, ContractItem, ContractItemUserType } from '../types.ts'
import { UserGroupsPanel } from './UserGroupsPanel.tsx'

interface PositionenTabProps {
  contract: ContractDetail
}

function isoDateOnly(d: string | null | undefined): string {
  if (!d) return ''
  return new Date(d).toISOString().slice(0, 10)
}

function fmtQty(x1000: number): string {
  return (x1000 / 1000).toLocaleString('de-DE', { maximumFractionDigits: 3 })
}

function parseQty(s: string): number | null {
  const trimmed = s.trim()
  if (!trimmed) return 0
  const n = parseFloat(trimmed.replace(/\./g, '').replace(',', '.'))
  if (Number.isNaN(n)) return null
  return Math.round(n * 1000)
}

export function PositionenTab({ contract }: PositionenTabProps) {
  const { toast } = useToast()
  const [addOpen, setAddOpen] = useState(false)
  const initFromTariff = useInitItemsFromTariff(contract.id)

  const items = contract.items ?? []
  const hasTariff = Boolean(contract.tariffId)

  return (
    <div className="space-y-4">
      <UserGroupsPanel contractId={contract.id} />
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-small text-gray-600">
          {items.length} Position{items.length === 1 ? '' : 'en'} im Vertrag
          {hasTariff ? ' · Tarif gesetzt' : ' · ohne Tarif'}
        </p>
        <div className="flex gap-2">
          {hasTariff ? (
            <Button
              variant="secondary"
              size="sm"
              leadingIcon={<Wand2 className="w-4 h-4" />}
              loading={initFromTariff.isPending}
              onClick={() =>
                initFromTariff.mutate(undefined, {
                  onSuccess: (r) => toast({ title: `${r.added} Positionen aus Tarif übernommen`, tone: 'success' }),
                  onError: (err) =>
                    toast({
                      title: 'Tarif-Übernahme fehlgeschlagen',
                      description: err instanceof Error ? err.message : 'Unbekannter Fehler',
                      tone: 'danger',
                    }),
                })
              }
            >
              Aus Tarif übernehmen
            </Button>
          ) : null}
          <Button variant="primary" size="sm" leadingIcon={<Plus className="w-4 h-4" />} onClick={() => setAddOpen(true)}>
            Position hinzufügen
          </Button>
        </div>
      </div>

      {items.length === 0 ? (
        <EmptyState
          title="Noch keine Positionen"
          description={
            hasTariff
              ? 'Du kannst die Komponenten des Tarifs auf einen Klick als Positionen übernehmen oder einzeln hinzufügen.'
              : 'Lege die ersten Positionen an. Pro Position pflegst du Soll-Mengen, Benutzer- und Asset-Zuordnungen.'
          }
        />
      ) : (
        <PositionTree items={items} contractId={contract.id} currency={contract.currency} />
      )}

      <AddPositionModal
        open={addOpen}
        onOpenChange={setAddOpen}
        contractId={contract.id}
        excludeProductIds={items.map((i) => i.productId)}
      />
    </div>
  )
}

/**
 * Stage 15: Hierarchical display of line items.
 * Items with parentItemId are indented under their parent entry.
 * Within the same level, items are sorted by `position`.
 */
function PositionTree({
  items,
  contractId,
  currency,
}: {
  items: ContractItem[]
  contractId: string
  currency: string
}) {
  // Build parent → children map
  const childrenByParent = useMemo(() => {
    const map = new Map<string | null, ContractItem[]>()
    for (const item of items) {
      const key = item.parentItemId ?? null
      const arr = map.get(key) ?? []
      arr.push(item)
      map.set(key, arr)
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => a.position - b.position)
    }
    return map
  }, [items])

  function renderLevel(parentId: string | null, depth: number): React.ReactNode {
    const children = childrenByParent.get(parentId) ?? []
    if (children.length === 0) return null
    return (
      <div className="space-y-3">
        {children.map((it) => (
          <div key={it.id} style={{ marginLeft: depth * 24 }}>
            <PositionRow contractId={contractId} item={it} currency={currency} />
            {renderLevel(it.id, depth + 1)}
          </div>
        ))}
      </div>
    )
  }

  return <div>{renderLevel(null, 0)}</div>
}

// ─── Line Item Row ─────────────────────────────────────────────────────────

interface PositionRowProps {
  contractId: string
  item: ContractItem
  currency: string
}

function PositionRow({ contractId, item, currency }: PositionRowProps) {
  const { toast } = useToast()
  const update = useUpdateContractItem(contractId)
  const del = useDeleteContractItem(contractId)
  const [description, setDescription] = useState(item.description ?? '')
  const [sollQty, setSollQty] = useState(fmtQty(item.sollQuantityX1000))
  const [discountPercent, setDiscountPercent] = useState(((item.discountPercentBp ?? 0) / 100).toFixed(2))
  const [serviceMemo, setServiceMemo] = useState(item.serviceMemo ?? '')
  const [itemStartDate, setItemStartDate] = useState(isoDateOnly(item.itemStartDate))
  const [itemTerminationEffectiveDate, setItemTerminationEffectiveDate] = useState(
    isoDateOnly(item.itemTerminationEffectiveDate),
  )
  const [isBillable, setIsBillable] = useState(item.isBillable !== false)
  const [quantityMode, setQuantityMode] = useState<'fixed' | 'per_user_group' | 'base_plus_per_user_group'>(
    item.quantityMode ?? 'fixed',
  )
  const [userGroupId, setUserGroupId] = useState(item.userGroupId ?? '')
  const [baseQty, setBaseQty] = useState(fmtQty(item.baseQuantityX1000 ?? 0))
  const [perUserQty, setPerUserQty] = useState(fmtQty(item.perUserQuantityX1000 ?? 0))
  const [parentItemId, setParentItemId] = useState(item.parentItemId ?? '')
  const [alsoSubscriptionId, setAlsoSubscriptionId] = useState(item.alsoSubscriptionId ?? '')
  const [m365LicenseSku, setM365LicenseSku] = useState(item.m365LicenseSku ?? '')
  const [esetLicenseId, setEsetLicenseId] = useState(item.esetLicenseId ?? '')
  const [tanssArtikelId, setTanssArtikelId] = useState(item.tanssArtikelId ?? '')
  const [itemSerialNumber, setItemSerialNumber] = useState(item.itemSerialNumber ?? '')
  const [showExtRefs, setShowExtRefs] = useState(
    !!(item.alsoSubscriptionId || item.m365LicenseSku || item.esetLicenseId || item.tanssArtikelId || item.itemSerialNumber),
  )
  const { data: contract } = useContract(item.contractId)
  const allItems = contract?.items ?? []
  const parentOptions = useMemo(
    () => [
      { value: '', label: '— Top-Level —' },
      ...allItems
        .filter((other) => other.id !== item.id)
        .map((other) => ({
          value: other.id,
          label: other.productName ?? other.description ?? '— Position —',
        })),
    ],
    [allItems, item.id],
  )
  const { data: userGroups = [] } = useContractUserGroups(contractId)
  const userGroupOptions = useMemo(
    () => [
      { value: '', label: '— Keine Klasse —' },
      ...userGroups.map((g) => ({ value: g.id, label: `${g.name} · ${g.count} User` })),
    ],
    [userGroups],
  )
  const [confirmDelete, setConfirmDelete] = useState(false)

  useEffect(() => {
    setDescription(item.description ?? '')
    setSollQty(fmtQty(item.sollQuantityX1000))
    setDiscountPercent(((item.discountPercentBp ?? 0) / 100).toFixed(2))
    setServiceMemo(item.serviceMemo ?? '')
    setItemStartDate(isoDateOnly(item.itemStartDate))
    setItemTerminationEffectiveDate(isoDateOnly(item.itemTerminationEffectiveDate))
    setIsBillable(item.isBillable !== false)
    setQuantityMode(item.quantityMode ?? 'fixed')
    setUserGroupId(item.userGroupId ?? '')
    setBaseQty(fmtQty(item.baseQuantityX1000 ?? 0))
    setPerUserQty(fmtQty(item.perUserQuantityX1000 ?? 0))
    setParentItemId(item.parentItemId ?? '')
    setAlsoSubscriptionId(item.alsoSubscriptionId ?? '')
    setM365LicenseSku(item.m365LicenseSku ?? '')
    setEsetLicenseId(item.esetLicenseId ?? '')
    setTanssArtikelId(item.tanssArtikelId ?? '')
    setItemSerialNumber(item.itemSerialNumber ?? '')
  }, [
    item.description,
    item.sollQuantityX1000,
    item.discountPercentBp,
    item.serviceMemo,
    item.itemStartDate,
    item.itemTerminationEffectiveDate,
    item.isBillable,
    item.quantityMode,
    item.userGroupId,
    item.baseQuantityX1000,
    item.perUserQuantityX1000,
    item.parentItemId,
    item.alsoSubscriptionId,
    item.m365LicenseSku,
    item.esetLicenseId,
    item.tanssArtikelId,
    item.itemSerialNumber,
  ])

  const dirty =
    description !== (item.description ?? '') ||
    sollQty !== fmtQty(item.sollQuantityX1000) ||
    Number(discountPercent.replace(',', '.')) * 100 !== (item.discountPercentBp ?? 0) ||
    serviceMemo !== (item.serviceMemo ?? '') ||
    itemStartDate !== isoDateOnly(item.itemStartDate) ||
    itemTerminationEffectiveDate !== isoDateOnly(item.itemTerminationEffectiveDate) ||
    isBillable !== (item.isBillable !== false) ||
    quantityMode !== (item.quantityMode ?? 'fixed') ||
    userGroupId !== (item.userGroupId ?? '') ||
    baseQty !== fmtQty(item.baseQuantityX1000 ?? 0) ||
    perUserQty !== fmtQty(item.perUserQuantityX1000 ?? 0) ||
    parentItemId !== (item.parentItemId ?? '') ||
    alsoSubscriptionId !== (item.alsoSubscriptionId ?? '') ||
    m365LicenseSku !== (item.m365LicenseSku ?? '') ||
    esetLicenseId !== (item.esetLicenseId ?? '') ||
    tanssArtikelId !== (item.tanssArtikelId ?? '') ||
    itemSerialNumber !== (item.itemSerialNumber ?? '')
  const unit = item.unit ?? item.productUnit ?? ''
  const ist = (item.users?.length ?? 0) + (item.assets?.length ?? 0)
  const sollNumber = item.sollQuantityX1000 / 1000
  const deltaWarning = sollNumber > 0 && ist !== sollNumber

  function handleSave() {
    const sollQuantityX1000 = parseQty(sollQty)
    if (sollQuantityX1000 == null) {
      toast({ title: 'Soll-Menge ungültig', tone: 'danger' })
      return
    }
    const discountBp = Math.max(0, Math.min(10000, Math.round(Number(discountPercent.replace(',', '.')) * 100) || 0))
    const baseX1000 = parseQty(baseQty) ?? 0
    const perUserX1000 = parseQty(perUserQty) ?? 0
    update.mutate(
      {
        itemId: item.id,
        input: {
          description: description || null,
          sollQuantityX1000,
          discountPercentBp: discountBp,
          serviceMemo: serviceMemo || null,
          itemStartDate: itemStartDate || null,
          itemTerminationEffectiveDate: itemTerminationEffectiveDate || null,
          isBillable,
          quantityMode,
          userGroupId: userGroupId || null,
          baseQuantityX1000: baseX1000,
          perUserQuantityX1000: perUserX1000,
          parentItemId: parentItemId || null,
          alsoSubscriptionId: alsoSubscriptionId || null,
          m365LicenseSku: m365LicenseSku || null,
          esetLicenseId: esetLicenseId || null,
          tanssArtikelId: tanssArtikelId || null,
          itemSerialNumber: itemSerialNumber || null,
        },
      },
      {
        onSuccess: () => toast({ title: 'Position gespeichert', tone: 'success' }),
        onError: (err) =>
          toast({
            title: 'Speichern fehlgeschlagen',
            description: err instanceof Error ? err.message : 'Unbekannter Fehler',
            tone: 'danger',
          }),
      },
    )
  }

  return (
    <Card className={item.isActive === false ? 'opacity-60' : undefined}>
      <Card.Header>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h4 className="text-body font-semibold text-gray-900">{item.productName ?? '— Produkt —'}</h4>
            <p className="text-caption text-gray-500">
              {unit ? `Einheit: ${unit}` : null}
              {item.unitPriceOverrideCents != null
                ? ` · Override-Preis: ${(item.unitPriceOverrideCents / 100).toLocaleString('de-DE', { style: 'currency', currency })}`
                : null}
              {item.paidUntil
                ? ` · Abgerechnet bis: ${new Date(item.paidUntil).toLocaleDateString('de-DE')}`
                : null}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {item.isActive === false ? (
              <Badge variant="neutral" size="sm">Inaktiv (entfernt)</Badge>
            ) : null}
            {deltaWarning ? (
              <Badge variant="warning" size="sm">
                Ist {ist} ≠ Soll {sollNumber}
              </Badge>
            ) : sollNumber > 0 ? (
              <Badge variant="success" size="sm">Ist = Soll</Badge>
            ) : null}
            <IconButton
              icon={<Trash2 className="w-4 h-4" />}
              aria-label="Position löschen"
              variant="ghost"
              size="sm"
              onClick={() => setConfirmDelete(true)}
            />
          </div>
        </div>
      </Card.Header>
      <Card.Body className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Input
            label="Beschreibung"
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
          />
          <Input
            label="Soll-Menge"
            value={sollQty}
            onChange={(e) => setSollQty(e.currentTarget.value)}
            trailingIcon={<span className="text-caption text-gray-500">{unit}</span>}
          />
          <Input
            label="Rabatt (%)"
            type="number"
            step="0.01"
            value={discountPercent}
            onChange={(e) => setDiscountPercent(e.currentTarget.value)}
          />
        </div>

        <Textarea
          label="Leistungsmemo"
          hint="Wird auf der Rechnung als Positionstext verwendet."
          rows={2}
          value={serviceMemo}
          onChange={(e) => setServiceMemo(e.currentTarget.value)}
        />

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Ab wann abrechenbar"
            hint="Position wird erst ab diesem Datum abgerechnet."
            type="date"
            value={itemStartDate}
            onChange={(e) => setItemStartDate(e.currentTarget.value)}
          />
          <Input
            label="Beendet am"
            hint="Soft-Delete-Stichtag — ab hier nicht mehr abrechenbar (leer = aktiv)."
            type="date"
            value={itemTerminationEffectiveDate}
            onChange={(e) => setItemTerminationEffectiveDate(e.currentTarget.value)}
          />
        </div>

        <div className="rounded border border-gray-200 bg-gray-50 p-3 space-y-3">
          <Select
            label="Eltern-Position (Bündel)"
            hint="Optional: Position als Kind unter eine andere einsortieren — die Hierarchie wird in der Liste eingerückt dargestellt."
            value={parentItemId}
            onValueChange={setParentItemId}
            options={parentOptions}
          />

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-small text-gray-700">
              <input
                type="checkbox"
                checked={isBillable}
                onChange={(e) => setIsBillable(e.currentTarget.checked)}
              />
              Abrechenbar
            </label>
            <p className="text-caption text-gray-500">
              Aus = Position wird nicht abgerechnet (z. B. Bündel-Eltern oder interne Hardware-Kalkulation).
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Select<'fixed' | 'per_user_group' | 'base_plus_per_user_group'>
              label="Mengen-Modus"
              value={quantityMode}
              onValueChange={setQuantityMode}
              options={[
                { value: 'fixed', label: 'Fest (Soll-Menge)' },
                { value: 'per_user_group', label: 'Pro User-Klasse' },
                { value: 'base_plus_per_user_group', label: 'Basis + pro User' },
              ]}
            />
            {quantityMode !== 'fixed' && (
              <Select
                label="User-Klasse"
                hint={
                  userGroups.length === 0
                    ? 'Lege oben eine User-Klasse an.'
                    : 'Effektive Menge wird automatisch aus deren Anzahl berechnet.'
                }
                value={userGroupId}
                onValueChange={setUserGroupId}
                options={userGroupOptions}
              />
            )}
          </div>

          {quantityMode === 'base_plus_per_user_group' && (
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Basis-Menge"
                hint="Sockel-Menge unabhängig von User-Anzahl."
                value={baseQty}
                onChange={(e) => setBaseQty(e.currentTarget.value)}
              />
              <Input
                label="Menge pro User"
                hint="Wird mit User-Anzahl multipliziert."
                value={perUserQty}
                onChange={(e) => setPerUserQty(e.currentTarget.value)}
              />
            </div>
          )}

          {quantityMode === 'per_user_group' && (
            <Input
              label="Menge pro User"
              hint="Wird mit User-Anzahl multipliziert. Beispiel: 1 = 1 Lizenz pro User."
              value={perUserQty}
              onChange={(e) => setPerUserQty(e.currentTarget.value)}
            />
          )}
        </div>

        <div>
          <button
            type="button"
            className="text-small text-gray-500 hover:text-gray-700 flex items-center gap-1 mb-2"
            onClick={() => setShowExtRefs((v) => !v)}
          >
            <span>{showExtRefs ? '▾' : '▸'}</span>
            <span>Externe System-Referenzen</span>
            {(item.alsoSubscriptionId || item.m365LicenseSku || item.esetLicenseId || item.tanssArtikelId || item.itemSerialNumber) && (
              <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-blue-500" />
            )}
          </button>
          {showExtRefs && (
            <div className="rounded border border-gray-200 bg-gray-50 p-3 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="ALSO Subscription-ID"
                  placeholder="ALSO-XXXXX"
                  value={alsoSubscriptionId}
                  onChange={(e) => setAlsoSubscriptionId(e.currentTarget.value)}
                />
                <Input
                  label="M365 Lizenz-SKU"
                  placeholder="SPE_E3"
                  value={m365LicenseSku}
                  onChange={(e) => setM365LicenseSku(e.currentTarget.value)}
                />
                <Input
                  label="ESET Lizenz-ID"
                  placeholder="ESET-XXXXX"
                  value={esetLicenseId}
                  onChange={(e) => setEsetLicenseId(e.currentTarget.value)}
                />
                <Input
                  label="TANSS Artikel-ID"
                  placeholder="TANSS-XXXXX"
                  value={tanssArtikelId}
                  onChange={(e) => setTanssArtikelId(e.currentTarget.value)}
                />
              </div>
              <Input
                label="Seriennummer (Hardware)"
                placeholder="SN-XXXXXXXX"
                value={itemSerialNumber}
                onChange={(e) => setItemSerialNumber(e.currentTarget.value)}
              />
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button variant="primary" size="sm" disabled={!dirty} loading={update.isPending} onClick={handleSave}>
            Speichern
          </Button>
        </div>

        <UsersBlock contractId={contractId} item={item} />
        <AssetsBlock contractId={contractId} item={item} />
        <CostLinksBlock contractId={contractId} item={item} />
        <HistoryBlock itemId={item.id} />
      </Card.Body>

      <ConfirmDialog
        open={confirmDelete}
        title={item.paidUntil ? 'Position aus Vertrag entfernen?' : 'Position löschen?'}
        message={
          item.paidUntil
            ? 'Position wurde bereits abgerechnet — sie wird zum heutigen Datum als beendet markiert (Soft-Delete) und bleibt für die Historie erhalten.'
            : 'Position wurde noch nie abgerechnet und kann inkl. Benutzer-/Asset-Zuordnungen physisch gelöscht werden.'
        }
        variant="danger"
        confirmLabel="Löschen"
        loading={del.isPending}
        onConfirm={() =>
          del.mutate(item.id, {
            onSuccess: () => {
              toast({ title: 'Position gelöscht', tone: 'success' })
              setConfirmDelete(false)
            },
            onError: (err) =>
              toast({
                title: 'Löschen fehlgeschlagen',
                description: err instanceof Error ? err.message : 'Unbekannter Fehler',
                tone: 'danger',
              }),
          })
        }
        onCancel={() => setConfirmDelete(false)}
      />
    </Card>
  )
}

// ─── User Assignment ───────────────────────────────────────────────────────

// ─── History (Stage 13c) ────────────────────────────────────────────────────

const ACTION_LABELS: Record<string, string> = {
  added: 'Hinzugefügt',
  removed: 'Entfernt',
  updated: 'Geändert',
  amount_changed: 'Menge geändert',
  discount_changed: 'Rabatt geändert',
  memo_changed: 'Memo geändert',
  lifecycle_changed: 'Lifecycle geändert',
}

function HistoryBlock({ itemId }: { itemId: string }) {
  const [open, setOpen] = useState(false)
  const { data: history = [] } = useContractItemHistory(open ? itemId : undefined)
  const { data: priceHistory = [] } = useContractItemPriceHistory(open ? itemId : undefined)

  return (
    <div className="border-t border-gray-100 pt-3">
      <button
        type="button"
        className="flex items-center gap-1 text-small text-gray-600 hover:text-gray-900"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <History className="w-4 h-4" />
        <span>Historie {open ? '' : 'anzeigen'}</span>
      </button>
      {open && (
        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <p className="text-caption font-medium text-gray-700 mb-1">Änderungen</p>
            {history.length === 0 ? (
              <p className="text-caption text-gray-400">— keine —</p>
            ) : (
              <ul className="space-y-1 text-caption">
                {history.map((h) => (
                  <li key={h.id} className="flex flex-col gap-0.5 border-l-2 border-gray-200 pl-2">
                    <div className="flex items-center justify-between text-gray-700">
                      <span className="font-medium">{ACTION_LABELS[h.actionType] ?? h.actionType}</span>
                      <span className="text-gray-400">{new Date(h.changedAt).toLocaleString('de-DE')}</span>
                    </div>
                    {h.reason && <span className="text-gray-500">{h.reason}</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <p className="text-caption font-medium text-gray-700 mb-1">Preis-Historie</p>
            {priceHistory.length === 0 ? (
              <p className="text-caption text-gray-400">— keine —</p>
            ) : (
              <ul className="space-y-1 text-caption">
                {priceHistory.map((p) => (
                  <li key={p.id} className="flex justify-between border-l-2 border-gray-200 pl-2">
                    <span className="text-gray-700">
                      ab {new Date(p.validFrom).toLocaleDateString('de-DE')}:{' '}
                      <span className="font-medium">
                        {(p.newPriceCents / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                      </span>
                      {p.oldPriceCents != null && (
                        <span className="text-gray-400">
                          {' '}
                          (vorher {(p.oldPriceCents / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })})
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

const USER_TYPE_LABELS: Record<ContractItemUserType, string> = {
  normal: 'Normal (0 %)',
  light: 'Geringe Nutzung (−30 %)',
  reduced: 'Abgespeckt (−70 %)',
}

const USER_TYPE_FACTORS: Record<ContractItemUserType, number> = {
  normal: 1.0,
  light: 0.7,
  reduced: 0.3,
}

const USER_TYPE_BADGE_VARIANT: Record<ContractItemUserType, 'neutral' | 'info' | 'warning'> = {
  normal: 'neutral',
  light: 'info',
  reduced: 'warning',
}

function fmtEur(cents: number): string {
  return (cents / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })
}

function UsersBlock({ contractId, item }: { contractId: string; item: ContractItem }) {
  const { toast } = useToast()
  const add = useAddItemUser(contractId)
  const remove = useRemoveItemUser(contractId)
  const updateType = useUpdateItemUserType(contractId)
  const [identifier, setIdentifier] = useState('')
  const [newUserType, setNewUserType] = useState<ContractItemUserType>('normal')
  const { data: allSetupFees = [] } = useContractSetupFees(contractId)
  const setupFees = allSetupFees.filter((f) => f.contractItemId === item.id)

  const baseUnitPriceCents = item.unitPriceOverrideCents ?? null

  function handleAdd() {
    if (!identifier.trim()) return
    add.mutate(
      { itemId: item.id, input: { userExternalId: identifier.trim(), userType: newUserType } },
      {
        onSuccess: () => {
          setIdentifier('')
          setNewUserType('normal')
          toast({ title: 'Benutzer ergänzt', tone: 'success' })
        },
        onError: (err) =>
          toast({
            title: 'Hinzufügen fehlgeschlagen',
            description: err instanceof Error ? err.message : 'Unbekannter Fehler',
            tone: 'danger',
          }),
      },
    )
  }

  function handleTypeChange(userId: string, userType: ContractItemUserType) {
    updateType.mutate(
      { itemId: item.id, userId, userType },
      {
        onError: (err) =>
          toast({
            title: 'Typ-Änderung fehlgeschlagen',
            description: err instanceof Error ? err.message : 'Unbekannter Fehler',
            tone: 'danger',
          }),
      },
    )
  }

  return (
    <div className="border-t border-gray-100 pt-3">
      <p className="text-small font-medium text-gray-700 mb-2">
        Benutzer-Zuordnung ({item.users?.length ?? 0})
      </p>
      {item.users && item.users.length > 0 ? (
        <div className="space-y-1.5 mb-3">
          {item.users.map((u) => {
            const userType: ContractItemUserType = (u.userType as ContractItemUserType) ?? 'normal'
            const effectiveCents =
              baseUnitPriceCents != null ? Math.round(baseUnitPriceCents * USER_TYPE_FACTORS[userType]) : null
            return (
              <div key={u.id} className="flex items-center gap-2">
                <Badge variant={USER_TYPE_BADGE_VARIANT[userType]} size="md" className="flex-none max-w-[200px] truncate">
                  {u.userExternalId ?? u.customerEmployeeId}
                </Badge>
                <Select
                  value={userType}
                  onValueChange={(v) => handleTypeChange(u.id, v as ContractItemUserType)}
                  triggerClassName="text-xs py-0.5 h-7"
                  disabled={updateType.isPending}
                  options={(Object.keys(USER_TYPE_LABELS) as ContractItemUserType[]).map((t) => ({
                    value: t,
                    label: USER_TYPE_LABELS[t],
                  }))}
                />
                {effectiveCents != null && (
                  <span className="text-caption text-gray-500 whitespace-nowrap">{fmtEur(effectiveCents)}/Einheit</span>
                )}
                <button
                  type="button"
                  aria-label="Entfernen"
                  onClick={() =>
                    remove.mutate(
                      { itemId: item.id, userId: u.id },
                      { onSuccess: () => toast({ title: 'Benutzer entfernt', tone: 'success' }) },
                    )
                  }
                  className="ml-auto text-gray-400 hover:text-gray-700 flex-none"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            )
          })}
        </div>
      ) : (
        <p className="text-caption text-gray-500 mb-2">Noch keine Benutzer zugeordnet.</p>
      )}
      {setupFees.length > 0 && (
        <div className="mt-2 mb-3 space-y-1">
          {setupFees.map((fee) => (
            <div key={fee.id} className="flex items-center gap-2 text-caption text-gray-600">
              <span className="font-medium">Setup-Pauschale:</span>
              <span>{(fee.amountCents / 100).toLocaleString('de-DE', { style: 'currency', currency: fee.currency })}</span>
              <Badge variant={fee.status === 'billed' ? 'success' : 'warning'} size="sm">
                {fee.status === 'billed' ? 'Abgerechnet' : 'Ausstehend'}
              </Badge>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2 items-end">
        <Input
          wrapperClassName="flex-1"
          placeholder="z. B. M365-UPN, AD-Login, TANSS-Mitarbeiter-ID"
          value={identifier}
          onChange={(e) => setIdentifier(e.currentTarget.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
        />
        <Select
          value={newUserType}
          onValueChange={(v) => setNewUserType(v as ContractItemUserType)}
          triggerClassName="text-sm"
          options={(Object.keys(USER_TYPE_LABELS) as ContractItemUserType[]).map((t) => ({
            value: t,
            label: USER_TYPE_LABELS[t],
          }))}
        />
        <Button variant="secondary" size="sm" onClick={handleAdd} loading={add.isPending} disabled={!identifier.trim()}>
          Hinzufügen
        </Button>
      </div>
    </div>
  )
}

// ─── Asset Assignment ──────────────────────────────────────────────────────

function AssetsBlock({ contractId, item }: { contractId: string; item: ContractItem }) {
  const { toast } = useToast()
  const add = useAddItemAsset(contractId)
  const remove = useRemoveItemAsset(contractId)
  const [identifier, setIdentifier] = useState('')
  const [assetType, setAssetType] = useState('')
  const [assetModel, setAssetModel] = useState('')
  const [assetLocation, setAssetLocation] = useState('')
  const [assetRefreshDate, setAssetRefreshDate] = useState('')

  function handleAdd() {
    if (!identifier.trim()) return
    add.mutate(
      {
        itemId: item.id,
        input: {
          assetExternalId: identifier.trim(),
          assetType: assetType.trim() || null,
          model: assetModel.trim() || null,
          location: assetLocation.trim() || null,
          refreshDate: assetRefreshDate || null,
        },
      },
      {
        onSuccess: () => {
          setIdentifier('')
          setAssetType('')
          setAssetModel('')
          setAssetLocation('')
          setAssetRefreshDate('')
          toast({ title: 'Asset ergänzt', tone: 'success' })
        },
        onError: (err) =>
          toast({
            title: 'Hinzufügen fehlgeschlagen',
            description: err instanceof Error ? err.message : 'Unbekannter Fehler',
            tone: 'danger',
          }),
      },
    )
  }

  return (
    <div className="border-t border-gray-100 pt-3">
      <p className="text-small font-medium text-gray-700 mb-2">
        Asset-Zuordnung ({item.assets?.length ?? 0})
      </p>
      {item.assets && item.assets.length > 0 ? (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {item.assets.map((a) => (
            <Badge key={a.id} variant="neutral" size="md">
              <span>{a.assetExternalId}</span>
              {a.assetType ? <span className="text-caption text-gray-500 ml-1">· {a.assetType}</span> : null}
              {a.model ? <span className="text-caption text-gray-400 ml-1">· {a.model}</span> : null}
              {a.location ? <span className="text-caption text-gray-400 ml-1">📍 {a.location}</span> : null}
              <button
                type="button"
                aria-label="Entfernen"
                onClick={() =>
                  remove.mutate(
                    { itemId: item.id, assetId: a.id },
                    {
                      onSuccess: () => toast({ title: 'Asset entfernt', tone: 'success' }),
                    },
                  )
                }
                className="ml-1 text-gray-400 hover:text-gray-700"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </Badge>
          ))}
        </div>
      ) : (
        <p className="text-caption text-gray-500 mb-2">Noch keine Assets zugeordnet.</p>
      )}
      <div className="grid grid-cols-2 gap-2">
        <Input
          placeholder="Externe ID / Gerätename"
          value={identifier}
          onChange={(e) => setIdentifier(e.currentTarget.value)}
        />
        <Input
          placeholder="Typ (cwa, eset, hardware …)"
          value={assetType}
          onChange={(e) => setAssetType(e.currentTarget.value)}
        />
        <Input
          placeholder="Modell (Dell Latitude 5540 …)"
          value={assetModel}
          onChange={(e) => setAssetModel(e.currentTarget.value)}
        />
        <Input
          placeholder="Büro / Raum"
          value={assetLocation}
          onChange={(e) => setAssetLocation(e.currentTarget.value)}
        />
        <Input
          type="date"
          placeholder="Refresh-Datum"
          value={assetRefreshDate}
          onChange={(e) => setAssetRefreshDate(e.currentTarget.value)}
        />
      </div>
      <div className="flex justify-end mt-2">
        <Button variant="secondary" size="sm" onClick={handleAdd} loading={add.isPending} disabled={!identifier.trim()}>
          Asset hinzufügen
        </Button>
      </div>
    </div>
  )
}

// ─── pass-through n:m links (CODA-650) ───────────────────────────────────────

function CostLinksBlock({ contractId, item }: { contractId: string; item: ContractItem }) {
  const { toast } = useToast()
  const { data: links = [], isLoading } = useContractItemLinks(contractId)
  const createLink = useCreateContractItemLink(contractId)
  const deleteLink = useDeleteContractItemLink(contractId)
  const [open, setOpen] = useState(false)

  const itemLinks = links.filter((l) => l.outboundItemId === item.id)

  return (
    <div className="border-t border-gray-100 pt-3">
      <div className="flex items-center justify-between mb-2">
        <p className="text-small font-medium text-gray-700">
          <Link2 className="inline w-3.5 h-3.5 mr-1 text-gray-400" />
          Eingehende Kosten ({itemLinks.length})
        </p>
        <Button variant="ghost" size="xs" leadingIcon={<Plus className="w-3.5 h-3.5" />} onClick={() => setOpen(true)}>
          Verknüpfen
        </Button>
      </div>
      {isLoading ? null : itemLinks.length === 0 ? (
        <p className="text-caption text-gray-400">Noch keine Einkaufsposition verknüpft.</p>
      ) : (
        <div className="space-y-1">
          {itemLinks.map((l) => (
            <div key={l.id} className="flex items-center justify-between gap-2 rounded bg-gray-50 px-2 py-1 text-caption">
              <span className="text-gray-700 truncate">
                {l.inboundContract?.contractNumber ?? '—'} {String.fromCharCode(183)} {l.inboundItem?.description ?? '—'}
              </span>
              <span className="shrink-0 text-gray-500">{l.allocationPercent}%</span>
              <button
                type="button"
                aria-label="Verknüpfung entfernen"
                className="text-gray-400 hover:text-red-500"
                onClick={() => deleteLink.mutate(l.id, { onSuccess: () => toast({ title: 'Verknüpfung entfernt', tone: 'success' }) })}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      <LinkInboundModal open={open} onOpenChange={setOpen} outboundItemId={item.id}
        onCreate={(input) => createLink.mutate(input, {
          onSuccess: () => { toast({ title: 'Verknüpfung gespeichert', tone: 'success' }); setOpen(false) },
          onError: (err) => toast({ title: 'Fehler beim Speichern', description: err instanceof Error ? err.message : 'Fehler', tone: 'danger' }),
        })}
        isPending={createLink.isPending}
      />
    </div>
  )
}

function LinkInboundModal({ open, onOpenChange, outboundItemId, onCreate, isPending }: {
  open: boolean; onOpenChange: (v: boolean) => void; outboundItemId: string
  onCreate: (input: { inboundItemId: string; outboundItemId: string; allocationPercent: number }) => void; isPending: boolean
}) {
  const { data: allContracts = [] } = useContracts({ direction: 'inbound' })
  const [selectedContractId, setSelectedContractId] = useState('')
  const { data: selectedContract } = useContract(selectedContractId || undefined)
  const [selectedItemId, setSelectedItemId] = useState('')
  const [allocationPercent, setAllocationPercent] = useState('100')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) { setSelectedContractId(''); setSelectedItemId(''); setAllocationPercent('100'); setError(null) }
  }, [open])

  const contractOptions = useMemo(
    () => [{ value: '', label: '— Eingehenden Vertrag wählen —' }, ...allContracts.map((c) => ({ value: c.id, label: `${c.contractNumber}${c.partyName ? ` ${String.fromCharCode(183)} ${c.partyName}` : ''}` }))],
    [allContracts],
  )
  const itemOptions = useMemo(
    () => [{ value: '', label: '— Position wählen —' }, ...(selectedContract?.items ?? []).map((it) => ({ value: it.id, label: it.description ?? it.productName ?? '— Position —' }))],
    [selectedContract],
  )

  function handleSave() {
    if (!selectedItemId) { setError('Bitte eine eingehende Position wählen.'); return }
    const pct = parseFloat(allocationPercent.replace(',', '.'))
    if (Number.isNaN(pct) || pct <= 0 || pct > 100) { setError('Anteil muss zwischen 0 und 100 liegen.'); return }
    onCreate({ inboundItemId: selectedItemId, outboundItemId, allocationPercent: pct })
  }

  return (
    <Modal open={open} onOpenChange={onOpenChange} size="sm" title="Eingehende Kosten verknüpfen"
      description="Welcher Einkaufsposten versorgt diese ausgehende Position?"
      footer={<><Button variant="secondary" size="sm" onClick={() => onOpenChange(false)} disabled={isPending}>Abbrechen</Button><Button variant="primary" size="sm" onClick={handleSave} loading={isPending}>Verknüpfen</Button></>}
    >
      <div className="space-y-3">
        {error ? <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-small text-red-700">{error}</div> : null}
        <Select label="Eingehender Vertrag" value={selectedContractId}
          onValueChange={(v) => { setSelectedContractId(v); setSelectedItemId('') }} options={contractOptions} />
        <Select label="Position im Eingangsvertrag" value={selectedItemId} onValueChange={setSelectedItemId}
          options={itemOptions} disabled={!selectedContractId} />
        <Input label="Anteil (%)" hint="Prozent des Einkaufspreises für diese Position" type="number" step="0.01" min="0.01" max="100"
          value={allocationPercent} onChange={(e) => setAllocationPercent(e.currentTarget.value)} />
      </div>
    </Modal>
  )
}

// ─── Add Line Item (Modal with Product Picker) ───────────────────────────────

interface AddPositionModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  contractId: string
  excludeProductIds: string[]
}

function AddPositionModal({ open, onOpenChange, contractId, excludeProductIds }: AddPositionModalProps) {
  const { toast } = useToast()
  const { data: products = [] } = useProducts({ active: true })
  const add = useAddContractItem(contractId)
  const [productId, setProductId] = useState('')
  const [description, setDescription] = useState('')
  const [sollQty, setSollQty] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setProductId('')
      setDescription('')
      setSollQty('')
      setError(null)
    }
  }, [open])

  const options = useMemo(
    () => [
      { value: '', label: '— Komponentenprodukt wählen —' },
      ...products
        .filter((p) => !excludeProductIds.includes(p.id))
        .map((p) => ({ value: p.id, label: `${p.name}${p.sku ? ` · ${p.sku}` : ''}` })),
    ],
    [products, excludeProductIds],
  )

  function handleSave() {
    if (!productId) {
      setError('Bitte ein Produkt wählen.')
      return
    }
    const sollQuantityX1000 = parseQty(sollQty)
    if (sollQuantityX1000 == null) {
      setError('Soll-Menge ungültig.')
      return
    }
    add.mutate(
      { productId, description: description || null, sollQuantityX1000 },
      {
        onSuccess: () => {
          toast({ title: 'Position ergänzt', tone: 'success' })
          onOpenChange(false)
        },
        onError: (err) => setError(err instanceof Error ? err.message : 'Anlegen fehlgeschlagen.'),
      },
    )
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="sm"
      title="Position hinzufügen"
      description="Welcher Vertragsbaustein und in welcher Soll-Menge?"
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={() => onOpenChange(false)} disabled={add.isPending}>
            Abbrechen
          </Button>
          <Button variant="primary" size="sm" onClick={handleSave} loading={add.isPending}>
            Hinzufügen
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {error ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-small text-red-700">
            {error}
          </div>
        ) : null}
        <Select label="Komponentenprodukt" value={productId} onValueChange={setProductId} options={options} />
        <Input
          label="Beschreibung"
          hint="optional, sonst wird der Produktname verwendet"
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
        />
        <Input
          label="Soll-Menge"
          hint="z. B. Anzahl Lizenzen oder Geräte"
          value={sollQty}
          onChange={(e) => setSollQty(e.currentTarget.value)}
        />
      </div>
    </Modal>
  )
}
